import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate, getAffiliate } from "@/lib/api";
import { hasAccess, useAuth } from "@/lib/auth";
import { Logo } from "@/components/AppShell";
import { Busy } from "@/components/ui-bits";
import { toast } from "sonner";
import { CreditCard, LockKeyhole } from "lucide-react";

export default function Billing() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/status").then(async (r) => {
      setStatus(r.data);
      if (r.data.has_access) { const u = await refresh(); if (hasAccess(u)) nav(u.onboarding_complete ? "/app" : "/onboarding", { replace: true }); }
    }).catch(() => {});
  }, [nav, refresh]);

  const updatePayment = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const resubscribe = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, affiliate_ref: getAffiliate() }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const canceled = status?.subscription_status === "canceled";

  return (
    <main className="min-h-screen bg-cream">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between"><Logo /><button onClick={async () => { await logout(); nav("/login"); }} className="text-sm text-ink-muted" data-testid="billing-logout">Log out</button></div>
      <div className="max-w-lg mx-auto px-5 py-12 animate-rise">
        <div className="card-soft p-8 text-center" data-testid="billing-restricted-card">
          <div className="size-14 rounded-full bg-clay-light text-clay grid place-items-center mx-auto"><LockKeyhole className="size-6" /></div>
          <p className="eyebrow mt-6">Billing</p>
          <h1 className="font-serif text-3xl mt-2">{canceled ? "Your membership has ended" : "Let's fix your payment"}</h1>
          <p className="text-ink-muted mt-3 text-sm">
            {canceled ? "Everything you built is saved: your voice profile, samples, vaults, calendar and emails. Reactivate to pick up right where you left off." : "We couldn't process your last payment, so Email Bestie is paused. Your data is completely safe. Update your card and access comes back automatically."}
          </p>
          <div className="mt-6 rounded-xl bg-linen border border-line p-4 text-left text-sm" data-testid="billing-status">
            <div className="flex justify-between"><span className="text-ink-muted">Status</span><span className="font-semibold capitalize">{status?.subscription_status?.replace("_", " ") || user?.subscription_status}</span></div>
            {status?.current_period_end && <div className="flex justify-between mt-2"><span className="text-ink-muted">{canceled ? "Ended" : "Period end"}</span><span className="font-semibold">{fmtDate(status.current_period_end, { month: "short", day: "numeric", year: "numeric" })}</span></div>}
            <div className="flex justify-between mt-2"><span className="text-ink-muted">Your price</span><span className="font-semibold">${status?.price ?? 17}/mo</span></div>
          </div>
          {canceled ? (
            <button onClick={resubscribe} disabled={busy} className="btn-ink w-full mt-6 !py-3.5" data-testid="billing-resubscribe-button">{busy ? <Busy /> : "Reactivate membership"}</button>
          ) : (<>
            <button onClick={updatePayment} disabled={busy} className="btn-ink w-full mt-6 !py-3.5" data-testid="billing-update-payment-button">{busy ? <Busy /> : <><CreditCard className="size-4" /> Update payment method</>}</button>
            <button onClick={resubscribe} disabled={busy} className="btn-ghost w-full mt-2" data-testid="billing-new-subscription-button">Start a new subscription instead</button>
          </>)}
          <p className="text-xs text-ink-soft mt-4">We never delete your data over a billing problem.</p>
        </div>
      </div>
    </main>
  );
}
